import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import { Platform } from "react-native";
import {
	ENDPOINT_ASSISTANT_SESSION_REVIEW,
	ENDPOINT_THREADS,
	GET_THREAD_QUERY_KEY,
	initialAiMessage,
} from "../constants";

import type { RecommendedQuestions } from "../../types";
import type { OriginalQuestionData } from "../types/question";
import type {
	EpubParams,
	ItemCacheIdentity,
	SSEEventData,
	Thread,
	VectorDocument,
	VectorStoreParams,
} from "./../types/";
import { resolveRecommendedQueriesCacheKey } from "./useRecommendedQueries";

import { safeJsonParse } from "@memorang/helpers/safe-json";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { randomUUID } from "expo-crypto";
import { useTranslation } from "react-i18next";
import EventSource from "react-native-sse";

import type {
	AiAssistantInfo,
	MessageData,
} from "@memorang/ui/components/ChatUI/types";
import {
	authHeaders,
	useAuthTokenGetter,
} from "@memorang/ui/contexts/AuthTokenContext";
//@ts-ignore
import packageJson from "../../../package.json";
import { getInitialQuestionMarkdown } from "../helpers";

type Props = {
	activityType: "review" | "reader";
	context: string;
	assistantInfo: AiAssistantInfo;
	outputLanguage: string;
	searchQuery?: {
		value: string;
	};
	appId?: string;
	vectorStoreParams: VectorStoreParams;
	sessionId?: string;
	userId?: string;
	epubParams?: EpubParams;
	topic?: string;
	originalQuestionData?: OriginalQuestionData;
	action?: "new" | null;
	userPrompt?: string;
	cacheIdentity?: ItemCacheIdentity | null;
	defaultInitialTitle?: string;
	initialFollowUps?: string[];
	handleResetDefaultQuery?: () => void;
	setSearchQuery: React.Dispatch<
		React.SetStateAction<{ value: string } | undefined>
	>;
};

const version = packageJson.version;

const resolveInitialGreeting = ({
	assistantInfo,
	outputLanguage,
	context,
	topic,
	t,
}: {
	assistantInfo: AiAssistantInfo;
	outputLanguage: string;
	context: string;
	topic: string;
	t: (key: string, options?: Record<string, string>) => string;
}) => {
	const localizedInitialMessage =
		assistantInfo.initialAiMessage?.[outputLanguage] ??
		(outputLanguage.startsWith("en")
			? assistantInfo.initialMessage
			: undefined);
	return localizedInitialMessage
		? initialAiMessage(localizedInitialMessage, context, topic, assistantInfo)
		: t("assistant.greeting.initialMessage", {
				assistantName: assistantInfo.alias,
				context: context ?? "your exam",
				topic,
			});
};

const useSearch = ({
	activityType,
	context,
	assistantInfo,
	searchQuery,
	appId,
	vectorStoreParams,
	sessionId,
	userId,
	epubParams,
	topic,
	originalQuestionData,
	action = null,
	userPrompt,
	cacheIdentity,
	defaultInitialTitle,
	initialFollowUps = [],
	outputLanguage,
	setSearchQuery,
	handleResetDefaultQuery,
}: Props) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const getAuthToken = useAuthTokenGetter();
	const [streaming, setStreaming] = useState(false);
	const [loading, setLoading] = useState(false);
	const [_isPending, startTransition] = useTransition();
	const [summaryResponse, setSummaryResponse] = useState<string | undefined>(
		undefined,
	);

	const eventSourceRef = useRef<EventSource | null>(null);

	const [references, setReferences] = useState<VectorDocument[] | []>([]);
	const [showReference, setShowReference] = useState<boolean | undefined>(
		undefined,
	);
	const [threadAction, setThreadAction] = useState(action);

	const { examId, itemId, contextId } = vectorStoreParams;

	const {
		isLoading: isThreadLoading,
		isRefetching: isThreadRefetching,
		data: thread,
	} = useQuery<Thread>({
		queryKey: [GET_THREAD_QUERY_KEY, examId, userId, itemId, threadAction],
		refetchOnWindowFocus: false,
		staleTime: 0,
		queryFn: async () => {
			const token = await getAuthToken();
			const result = await fetch(ENDPOINT_THREADS, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...authHeaders(token),
				},
				body: JSON.stringify({
					assistantId: `${examId}`,
					userId: userId,
					itemId: itemId,
					activityType,
					...(threadAction ? { action: threadAction } : {}),
				}),
			});
			const body = await result.json();
			body.initialTitle = defaultInitialTitle;
			// The recommended-queries cache is populated elsewhere (no queryFn
			// here); a miss must degrade to no follow-ups, never fail the thread.
			const { topic, followUps } = await queryClient
				.ensureQueryData<RecommendedQuestions>({
					queryKey: resolveRecommendedQueriesCacheKey({
						userPrompt,
						cacheIdentity,
						examId,
						itemId,
						outputLanguage,
					}),
				})
				.catch(() => ({ topic: undefined, followUps: undefined }));
			if (topic && followUps) {
				const initialMessageData: MessageData = {
					id: randomUUID(),
					type: "assistant",
					content: resolveInitialGreeting({
						assistantInfo,
						outputLanguage,
						context,
						topic,
						t,
					}),
					loading: false,
					streaming: false,
					references:
						originalQuestionData && assistantInfo.showOriginalQuestion
							? getInitialQuestionMarkdown(originalQuestionData)
							: undefined,
				};
				body.messages = [initialMessageData, ...body.messages];
				body.messages[body.messages.length - 1].followUps = followUps;
				body.initialTitle = topic;
			}
			if (!followUps && body.messages.length > 0) {
				body.messages[body.messages.length - 1].followUps = [
					...initialFollowUps,
				];
			}
			return body;
		},
	});

	const setLocalMessages = (
		updateFunctionOrValue:
			| ((prevMessages: MessageData[]) => MessageData[])
			| MessageData[],
	) => {
		queryClient.setQueryData<Thread | undefined>(
			[GET_THREAD_QUERY_KEY, examId, userId, itemId, threadAction],
			(prevData) => {
				if (prevData) {
					if (Array.isArray(updateFunctionOrValue)) {
						prevData.messages = updateFunctionOrValue;
					} else {
						prevData.messages = updateFunctionOrValue(prevData.messages);
					}
				}
				return prevData;
			},
		);
	};
	const removeLastMessage = () => {
		queryClient.setQueryData<Thread | undefined>(
			[GET_THREAD_QUERY_KEY, examId, userId, itemId, threadAction],
			(prevData) => {
				setLoading(true);
				if (prevData) {
					prevData.messages = [
						...prevData.messages.slice(0, -3),
						{
							...prevData.messages[prevData.messages.length - 3],
							followUps: undefined,
						},
						prevData.messages[prevData.messages.length - 2],
						{
							id: randomUUID(),
							type: "assistant",
							content: "",
							tools: undefined,
							followUps: undefined,
							// @ts-ignore
							loading: true,
						},
					];
				}
				return prevData;
			},
		);
	};
	const addMessage = (message: MessageData) => {
		queryClient.setQueryData<Thread | undefined>(
			[GET_THREAD_QUERY_KEY, examId, userId, itemId, threadAction],
			(prevData) => {
				if (prevData) {
					prevData.messages = [...prevData.messages, message];
				}
				return prevData;
			},
		);
	};

	const { mutate: mutateSummary } = useMutation({
		mutationFn: async ({
			examId,
			query,
			cacheKey,
			epubProps,
			threadId,
			originalQuestionData,
			userPrompt,
			outputLanguage,
			appId,
		}: {
			examId: string;
			query: string;
			cacheKey: string;
			threadId: string;
			epubProps?: EpubParams;
			originalQuestionData?: OriginalQuestionData;
			userPrompt?: string;
			outputLanguage?: string;
			appId?: string;
		}) => {
			setLoading(true);
			setReferences([]);
			const originalQuestion = originalQuestionData && {
				stem: originalQuestionData.stem,
				explanation: originalQuestionData.explanation,
				topics: originalQuestionData.topics,
				selectedChoice: originalQuestionData.selectedChoice,
				correctChoice: originalQuestionData.correctChoice,
				incorrectChoices: originalQuestionData.incorrectChoices,
			};
			const token = await getAuthToken();
			const es = new EventSource(ENDPOINT_ASSISTANT_SESSION_REVIEW, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					Accept: "text/event-stream",
					...authHeaders(token),
				},
				body: JSON.stringify({
					query,
					cacheKey,
					scope: epubProps?.scope,
					isSummarize: epubProps?.isSummarize,
					pageText: epubProps?.pageText,
					selectedText: epubProps?.selectedText,
					filterSourceIds: epubProps?.filterSourceIds,
					platform: Platform.OS,
					examId: `${examId}`,
					assistantId: `${examId}`,
					threadId,
					originalQuestion,
					// TODO: Remove when reader uses the new API
					categoryId: [`${examId}`],
					sessionId: sessionId || threadId,
					userId,
					itemId,
					contextId,
					topic,
					...(epubProps || {}),
					extraDetails: userPrompt,
					outputLanguage,
					appId,
				}),
			});
			eventSourceRef.current = es;

			es.addEventListener("open", (_event) => {
				setSearchQuery({ value: "" });
				handleResetDefaultQuery?.();
			});

			// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
			es.addEventListener("message", (event) => {
				if (!event.data) {
					return;
				}
				const data = safeJsonParse<SSEEventData>(event.data);
				if (!data) {
					return;
				}

				if (data.type === "run-details") {
					startTransition(() => {
						// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
						setLocalMessages((prevMessages) => {
							const lastMessage = prevMessages[prevMessages.length - 1];
							if (!lastMessage) {
								return prevMessages;
							}

							if (data.showSources && data.sources && data.sources.length > 0) {
								setShowReference(true);
								lastMessage.references = data.sources;
							}

							if (data.tools) {
								lastMessage.tools = {
									...data.tools,
								};
							}

							if (!lastMessage.runId) {
								lastMessage.runId = data.runId;
							}
							return [...prevMessages];
						});
					});
					return;
				}
				if (data?.close) {
					es.close();
					return;
				}

				if (!streaming) {
					setLoading(false);
					setStreaming(true);
					setLocalMessages((prevMessages) => {
						const lastMessage = prevMessages[prevMessages.length - 1];
						if (lastMessage) {
							lastMessage.loading = false;
							lastMessage.streaming = true;
						}
						return [...prevMessages];
					});
				}

				if (data.followUps) {
					setLocalMessages((prevMessages) => {
						const lastMessage = prevMessages[prevMessages.length - 1];
						lastMessage.followUps = data.followUps;
						return [...prevMessages];
					});
				}

				if (data.response) {
					const response = data.response;
					setLocalMessages((prevMessages) => {
						const lastMessage = prevMessages[prevMessages.length - 1];
						lastMessage.content = response;
						return [...prevMessages];
					});

					setSummaryResponse(response);
				}
			});

			es.addEventListener("close", (_event) => {
				eventSourceRef.current = null;
				setStreaming(false);
				setLocalMessages((prevMessages) => {
					const lastMessage = prevMessages[prevMessages.length - 1];
					if (
						!lastMessage ||
						lastMessage.type !== "assistant" ||
						!lastMessage.id
					) {
						return prevMessages;
					}
					lastMessage.streaming = false;
					lastMessage.onPressRetry = () => {
						removeLastMessage();
						setLoading(false);
						setStreaming(false);
						setSummaryResponse(undefined);
						getSummary();
					};
					return [...prevMessages];
				});
			});

			es.addEventListener("error", (event) => {
				if (event.type === "error") {
					console.error("Connection error:", event.message);
					const errorId = randomUUID();
					setLocalMessages((prevMessages) => {
						return [
							...prevMessages,
							{
								id: errorId,
								type: "assistant",
								content: "",
								loading: false,
								streaming: false,
								errorMessage: event.message,
								onPressRetry: () => {
									setLocalMessages((currentMessages) =>
										currentMessages.filter((msg) => msg.id !== errorId),
									);
									setLoading(false);
									setStreaming(false);
									setSummaryResponse(undefined);
									getSummary();
								},
							},
						];
					});
					setLoading(false);
					setStreaming(false);
				} else if (event.type === "exception") {
					console.error("Error:", event.message, event.error);
					const errorId = randomUUID();
					setLocalMessages((prevMessages) => {
						return [
							...prevMessages,
							{
								id: errorId,
								type: "assistant",
								content: "",
								loading: false,
								streaming: false,
								errorMessage: event.message,
								onPressRetry: () => {
									removeLastMessage();
									setLoading(false);
									setStreaming(false);
									setSummaryResponse(undefined);
									getSummary();
								},
							},
						];
					});
					setLoading(false);
					setStreaming(false);
				}
			});
		},
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	const abortSearch = useCallback(async () => {
		if (eventSourceRef.current) {
			eventSourceRef.current?.close();
			setLoading(false);
			if (loading && !streaming) {
				setSummaryResponse(undefined);
				setLocalMessages((prevMessages) => {
					const lastMessage = prevMessages[prevMessages.length - 1];
					if (lastMessage?.loading) {
						prevMessages.pop();
					}
					return [...prevMessages];
				});
			}
			setStreaming(false);
		}
	}, [loading, streaming, queryClient]);

	const getSummary = async () => {
		if (examId && searchQuery?.value && vectorStoreParams) {
			const data = await queryClient.ensureQueryData<Thread | undefined>({
				queryKey: [GET_THREAD_QUERY_KEY, examId, userId, itemId, threadAction],
			});
			if (data) {
				const cacheKey = `${version} : ${JSON.stringify(vectorStoreParams)} : ${
					searchQuery.value
				}`;
				mutateSummary({
					examId: `${examId}`,
					query: searchQuery.value,
					cacheKey,
					epubProps: epubParams,
					threadId: data.id,
					originalQuestionData,
					userPrompt,
					outputLanguage,
					appId,
				});
				return () => {
					eventSourceRef.current?.close();
				};
			}
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	useEffect(() => {
		getSummary();
	}, [searchQuery?.value]);

	const resetSearch = useCallback(
		(shouldResetMessages = false) => {
			if (shouldResetMessages) {
				if (threadAction === "new") {
					queryClient.invalidateQueries({
						queryKey: ["thread"],
					});
				} else {
					setThreadAction("new");
				}
			}
			setSummaryResponse(undefined);
			setStreaming(false);
			setReferences([]);
		},
		[queryClient, threadAction],
	);

	const lastFollowUps =
		thread?.messages?.[thread?.messages.length - 1]?.followUps;

	const secondLastFollowUps =
		thread?.messages?.[thread?.messages.length - 3]?.followUps;

	return {
		loading: loading || isThreadLoading || isThreadRefetching,
		summaryResponse,
		references,
		streaming,
		setMessages: setLocalMessages,
		addMessage,
		messages: thread?.messages || [],
		setSummaryResponse,
		resetSearch,
		showReference,
		abortSearch,
		isThreadLoading: isThreadLoading || isThreadRefetching,
		thread,
		initialTitle: thread?.initialTitle,
		followUps: lastFollowUps?.length
			? lastFollowUps
			: secondLastFollowUps || [],
	};
};

export { useSearch };
